import { getRuleScopeOfferExclusions } from "@/util/pricingRules";
import { getPricingRules } from "@/api/pricingRules";
import { rulesEnum } from "@/util/enums/rules";
import { costAdditionScope } from "@/util/enums/costAdditionScope";

const rules = {
  namespaced: true,
  state: {
    globalRules: {},
    costAdditionRules: [],
    bioCostAdditionRules: [],
    temperatureCostAdditionRules: [],
    customerMarginRules: [],
    offerSpecificRules: {
      costAdditionRules: [],
      bioCostAdditionRules: [],
      temperatureCostAdditionRules: [],
      customerMarginRules: [],
    },
    rulesToExcludeFromCalculation: [],
    ruleValidationThreshold: {
      MinTemperatureCelsius: 0,
      MaxTemperatureCelsius: 32,
    },
    eRuleExclusionType: {
      Removed: "REMOVED",
      Overridden: "OVERRIDDEN",
      Reset: "RESET",
    },
    isRuleRemovalConfirmed: false,
    deletedRuleScope: null,
    costAdditionRulesPaged: [],
    bioCostAdditionRulesPaged: [],
    temperatureCostAdditionRulesPaged: [],
    customerMarginRulesPaged: [],
    pagedData: {},
    defaultPagedMetadata: {
      pageCount: 0,
      pageLimit: 25,
      pageNumber: 1,
      total: 0,
    }
  },
  getters: {
    globalRulesGetter: function (state) {
      return state.globalRules;
    },
    costAdditionRulesGetter: function (state) {
      return state.costAdditionRules;
    },
    bioCostAdditionRulesGetter: function (state) {
      return state.bioCostAdditionRules;
    },
    temperatureCostAdditionRulesGetter: function (state) {
      return state.temperatureCostAdditionRules;
    },
    customerMarginRulesGetter: function (state) {
      return state.customerMarginRules;
    },
    offerSpecificRules: function (state) {
      return state.offerSpecificRules;
    },
    ruleValidationThreshold: function (state) {
      return state.ruleValidationThreshold;
    },
    eRuleExclusionType: function (state) {
      return state.eRuleExclusionType;
    },
    isRuleRemovalConfirmed: function (state) {
      return state.isRuleRemovalConfirmed;
    },
    deletedRuleScope: function (state) {
      return state.deletedRuleScope;
    },
    costAdditionRulesPagedGetter: function (state) {
      return state.costAdditionRulesPaged;
    },
    bioCostAdditionRulesPagedGetter: function (state) {
      return state.bioCostAdditionRulesPaged;
    },
    temperatureCostAdditionRulesPagedGetter: function (state) {
      return state.temperatureCostAdditionRulesPaged;
    },
    customerMarginRulesPagedGetter: function (state) {
      return state.customerMarginRulesPaged;
    },
    getPageMetadata: function (state) {
      return state.pagedData;
    },
  },
  actions: {
    getGlobalRules: async function ({ commit, rootState }) {
      await rootState.axios.get("/api/pricing/rules/global").then((res) => {
        commit("SET_GLOBAL_RULES", res.data);
      });
    },
    getCostAdditionRules: async function ({ commit, rootGetters }) {
      await getPricingRules({ pageType: "COST_ADDITIONS_ALL" }).then((costAdditionRules) => {
        const payload = {
          productConfig: rootGetters["metadata/productConfig"],
          costAdditionRules,
        };
        commit("SET_COST_ADDITION_RULES", payload);
      });
    },
    getGlobalSpecifiedRulesPaged: async function ({ commit }, payload) {
      await getPricingRules({ pageType: payload.pageType, pageLimit: Number(payload.pageLimit), pageNumber: Number(payload.pageNumber), priority: payload.priority }).then((costAdditionRules) => {
        switch (payload.pageType) {
          case "COST_ADDITIONS": {
            commit("SET_COST_ADDITION_RULES_PAGED", costAdditionRules);
            break;
          }
          case "BIO_COST_ADDITIONS": {
            commit("SET_COST_ADDITION_RULES_BIO_PAGED", costAdditionRules);
            break;
          }
          case "TEMPERATURE": {
            commit("SET_COST_ADDITION_RULES_TEMP_PAGED", costAdditionRules);
            break;
          }
          case "CUSTOMER": {
            commit("SET_COST_ADDITION_RULES_CUSTOMER_PAGED", costAdditionRules);
            break;
          }
        }
      });
    },
    confirmRuleRemoval: function ({ commit }, isConfirmed) {
      commit("CONFIRM_RULE_REMOVAL", isConfirmed);
    },
    setDeletedRuleScope: function ({ commit }, ruleScope) {
      commit("SET_DELETED_RULE_SCOPE", ruleScope);
    },
  },
  mutations: {
    SET_GLOBAL_RULES: function (state, globalRules) {
      // Let's ensure that values are displayed with correct decimal places first
      for (let rule of globalRules.dailySettlementRules) {
        const { targetAttribute, value } = rule.action;
        rule.action.value = value
          ? Number(value).toFixed(targetAttribute === rulesEnum.eTargetAttributeType.Fx ? 4 : 2)
          : "";
      }

      for (let rule of [].concat.apply([], Object.values(globalRules.productRules))) {
        const { value } = rule.action;
        rule.action.value = Number(value).toFixed(2);
      }

      let iceLivePremiumRule = globalRules.iceDataRules.find(
        (rule) => rule.action.targetAttribute === rulesEnum.eTargetAttributeType.IceLivePremium
      );
      iceLivePremiumRule.action.value = iceLivePremiumRule.action.value
        ? Number(iceLivePremiumRule.action.value).toFixed(2)
        : "";

      // Now, we can assign the updated values
      state.globalRules = globalRules;
    },
    SET_COST_ADDITION_RULES: function (state, payload) {
      const { productConfig, costAdditionRules } = payload;
      for (let rule of costAdditionRules) {
        // Assign value to Number
        const { value } = rule.action;
        rule.action.value = Number(value).toFixed(2);
      }

      // Let's get the bio product IDs to get the bio rules
      const bioProductIds = [productConfig.BioDieselId, productConfig.BioDieselForGasoilId];

      // Filter CUSTOMER MARGIN rules
      const customerMarginOfferExclusions = getRuleScopeOfferExclusions(
        costAdditionScope.Customer,
        costAdditionScope,
        rulesEnum.eOfferExclusion
      );
      const customerMarginRules = costAdditionRules.filter(
        (rule) =>
          rule.targets.find((target) => target.type === rulesEnum.eTargetType.Account) && // Rules have to target account(s)
          customerMarginOfferExclusions.every((exclusion) => rule.offerExclusions.includes(exclusion)) // Rules need to have customer margin rules' offer exclusions
      );
      state.offerSpecificRules.customerMarginRules = customerMarginRules.filter(
        (rule) => rule.priority === rulesEnum.ePriority.Offer
      );
      state.customerMarginRules = customerMarginRules.filter(
        (rule) => !state.offerSpecificRules.customerMarginRules.includes(rule)
      );

      // Filter NON-CUSTOMER MARGIN rules
      const nonCustomerMarginRules = costAdditionRules.filter((rule) => !customerMarginRules.includes(rule));

      // Filter BIO COST ADDITION rules
      const bioCostAdditionRules = nonCustomerMarginRules.filter(
        (rule) =>
          rule.targets.find((target) => target.type === rulesEnum.eTargetType.Product) &&
          rule.targets.find((target) => target.ids.find((id) => bioProductIds.includes(id)))
      );
      state.offerSpecificRules.bioCostAdditionRules = bioCostAdditionRules.filter(
        (rule) => rule.priority === rulesEnum.ePriority.Offer
      );
      state.bioCostAdditionRules = bioCostAdditionRules.filter(
        (rule) => !state.offerSpecificRules.bioCostAdditionRules.includes(rule)
      );

      // Filter TEMPERATURE COST ADDITION rules
      const temperatureCostAdditionRules = nonCustomerMarginRules.filter((rule) =>
        rule.offerExclusions.includes(rulesEnum.eOfferExclusion.StandardMeasurement)
      );
      state.offerSpecificRules.temperatureCostAdditionRules = temperatureCostAdditionRules.filter(
        (rule) => rule.priority === rulesEnum.ePriority.Offer
      );
      state.temperatureCostAdditionRules = temperatureCostAdditionRules.filter(
        (rule) => !state.offerSpecificRules.temperatureCostAdditionRules.includes(rule)
      );

      // Filter REMAINING COST ADDITION rules
      const remainingCostAdditionRules = nonCustomerMarginRules.filter(
        (rule) => !bioCostAdditionRules.includes(rule) && !temperatureCostAdditionRules.includes(rule)
      );
      state.offerSpecificRules.costAdditionRules = remainingCostAdditionRules.filter(
        (rule) => rule.priority === rulesEnum.ePriority.Offer
      );
      state.costAdditionRules = remainingCostAdditionRules.filter(
        (rule) => !state.offerSpecificRules.costAdditionRules.includes(rule)
      );
    },
    SET_COST_ADDITION_RULES_PAGED: function (state, payload) {
      state.costAdditionRulesPaged = payload.rules ? payload.rules : [];
      state.pagedData = payload.metadata ? payload.metadata : state.defaultPagedMetadata;
    },
    SET_COST_ADDITION_RULES_BIO_PAGED: function (state, payload) {
      state.bioCostAdditionRulesPaged = payload.rules ? payload.rules : [];
      state.pagedData = payload.metadata ? payload.metadata : state.defaultPagedMetadata;
    },
    SET_COST_ADDITION_RULES_TEMP_PAGED: function (state, payload) {
      state.temperatureCostAdditionRulesPaged = payload.rules ? payload.rules : [];
      state.pagedData = payload.metadata ? payload.metadata : state.defaultPagedMetadata;
    },
    SET_COST_ADDITION_RULES_CUSTOMER_PAGED: function (state, payload) {
      state.customerMarginRulesPaged = payload.rules ? payload.rules : [];
      state.pagedData = payload.metadata ? payload.metadata : state.defaultPagedMetadata;
    },
    CONFIRM_RULE_REMOVAL: function (state, isConfirmed) {
      state.isRuleRemovalConfirmed = isConfirmed;
    },
    SET_DELETED_RULE_SCOPE: function (state, ruleScope) {
      state.deletedRuleScope = ruleScope;
    },
  },
};

export default rules;